/*
 * @providesModule LiveBracketToastController
 *
 */

import Bracket, {BRACKET_STATUS, BRACKET_PLAYER_STATE} from 'SKZBracket';
import Tournament from 'SKZTournament';
import ClientPropertyConstants from 'ClientPropertyConstants';
import moment from 'moment';
import {_m} from 'INTL';
import {getRegisteredLiveBrackets} from 'LiveBracketDataController';
import {fetchBracketMeta} from 'BracketMetaAPI';

export const QUERY_KEYS = {
  getLiveBracket: 'getLiveBracket',
};

export type LiveBracketToastState =
  | 'hidden'
  | 'upcoming'
  | 'ready-to-play'
  | 'waiting-for-opponent';

export default class LiveBracketToastController {
  static async getLiveBracket(useCache = false): Promise<Tournament | null> {
    const registeredLiveBrackets = await getRegisteredLiveBrackets(useCache);

    if (!registeredLiveBrackets.length) {
      return Promise.resolve(null);
    }

    /**
     * If multiple registered live brackets are found, we get the most recent one
     */
    if (registeredLiveBrackets.length > 1) {
      const [mostRecentLiveBracket] = registeredLiveBrackets.sort(
        (a: Tournament, b: Tournament) => {
          return moment(a.start).diff(moment(b.start));
        }
      );
      return Promise.resolve(mostRecentLiveBracket || null);
    }

    return Promise.resolve(registeredLiveBrackets[0] || null);
  }

  static async getTournamentAndBracket(): Promise<{
    tournament: Tournament | null;
    bracket: Bracket | null;
    currentRound?: number;
  }> {
    const tournament = await LiveBracketToastController.getLiveBracket(false);

    if (!tournament) {
      return {tournament: null, bracket: null};
    }

    if (!tournament.isPlayerRegistered()) {
      return {tournament: null, bracket: null};
    }

    if (!tournament.bracket?.id) {
      return {tournament: null, bracket: null};
    }

    const bracket = await LiveBracketToastController.getBracketAsPromise(tournament.bracket.id);

    // Get current round from the bracket data
    let currentRound = 1; // Default fallback
    if (bracket && (bracket as any).currentRound) {
      currentRound = (bracket as any).currentRound;
    }

    return {tournament, bracket, currentRound};
  }

  static getBracketAsPromise(bracketId?: string): Promise<Bracket> {
    if (!bracketId) {
      return Promise.reject(new Error('Bracket ID is required'));
    }

    return new Promise(async (resolve, reject) => {
      try {
        // Use the new v2 endpoint
        const response = await fetchBracketMeta(bracketId, 1);

        if (!response || !response.data) {
          reject(new Error('Failed to fetch bracket data from v2 API'));
          return;
        }

        const {bracket_meta, matches, current_player_info} = response.data;

        // Map v2 state to v1 BracketState
        const mapBracketState = (v2State: string) => {
          switch (v2State) {
            case 'OPEN':
              return BRACKET_STATUS.OPEN;
            case 'FULL':
              return BRACKET_STATUS.FULL;
            case 'CONCLUDED':
              return BRACKET_STATUS.CONCLUDED;
            case 'CANCELED':
              return BRACKET_STATUS.CANCELED;
            default:
              return BRACKET_STATUS.OPEN;
          }
        };

        // Map v2 player state to v1 BracketPlayerState
        const mapPlayerState = (v2PlayerState: string) => {
          switch (v2PlayerState) {
            case 'REGISTERED':
            case 'READY_TO_JOIN':
              return BRACKET_PLAYER_STATE.READY;
            case 'JOINED':
            case 'WAITING_ROUND_END':
              return BRACKET_PLAYER_STATE.PENDING;
            case 'FINISHED':
            case 'FINISHED_CANCELED':
              return BRACKET_PLAYER_STATE.FINISHED;
            default:
              return BRACKET_PLAYER_STATE.PENDING;
          }
        };

        // Convert v2 response to v1 Bracket format
        const bracketRecord = {
          id: bracket_meta.bracket_id.toString(),
          size: bracket_meta.size,
          num_entries: bracket_meta.num_entries,
          tournament_id: bracket_meta.tournament_template_id.toString(),
          updated_at: new Date().toISOString(),
          cancel_at: bracket_meta.bracket_end_time,
          state: mapBracketState(bracket_meta.state),
          player: current_player_info
            ? {
                id: (current_player_info as any).bracket_player_id?.toString() || 'temp_player_id',
                user_id: (current_player_info as any).bracket_player_id || 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                expires_at:
                  (current_player_info as any).expires_at || bracket_meta.bracket_end_time,
                state: mapPlayerState(current_player_info.state),
                starting_position: (current_player_info as any).starting_position || 1,
                latest_position: (current_player_info as any).latest_position || 1,
              }
            : {
                id: 'no_player',
                user_id: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                expires_at: bracket_meta.bracket_end_time,
                state: BRACKET_PLAYER_STATE.PENDING,
                starting_position: 1,
                latest_position: 1,
              },
          matches: matches || [],
          current_player_info: current_player_info || null,
        };

        const bracket = new Bracket(bracketRecord);

        // Add current round information to the bracket object
        (bracket as any).currentRound = bracket_meta.current_round || 1;

        resolve(bracket);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Provides the state of the LiveBracketToast based on the tournament data
   * @param tournamentData tournament data from tournament-categories API
   * @returns `LiveBracketToastState`
   */
  static getLiveBracketToastStateFromTournament(
    tournamentData?: Tournament | null
  ): LiveBracketToastState {
    if (!tournamentData) {
      console.debug('LiveBracketToastController: STATE hidden - No tournament found');
      return 'hidden';
    }

    if (tournamentData.hasTournamentFinished()) {
      console.debug('LiveBracketToastController: STATE hidden - Tournament finished');
      return 'hidden';
    }

    /**
     * Bracket finished: The player has lost the match, won the tournament or the tournament has finished
     */
    if (tournamentData.isPlayerMatchFinished()) {
      console.debug('LiveBracketToastController: STATE hidden - Bracket finished');
      return 'hidden';
    }

    /**
     * Removed early start condition when bracket is full
     * Now tournaments only start at their scheduled time, not when bracket fills up
     * This ensures the countdown timer always shows until the scheduled start time
     */

    const showDrawerBeforeStartSeconds =
      ClientPropertyConstants.getLiveBracketsShowToastBeforeStartSeconds();
    const remainingTimeToStart = moment(tournamentData.start).diff(moment(), 'seconds');

    // Check if tournament has actually started at its scheduled time, not just if bracket is full
    const actuallyStarted =
      tournamentData.start && new Date(tournamentData.start).getTime() <= Date.now();

    /**
     * PRIORITY 1: If the tournament has not started yet, always show 'upcoming' state with countdown
     * This prevents any bracket state from overriding the countdown timer before tournament starts
     */
    if (
      !actuallyStarted &&
      tournamentData.isPlayerRegistered() &&
      remainingTimeToStart <= showDrawerBeforeStartSeconds
    ) {
      // console.debug(
      //   'LiveBracketToastController: STATE upcoming - Tournament not started, showing countdown'
      // );
      return 'upcoming';
    }

    /**
     * PRIORITY 2: If the tournament has started and the player is ready to play
     * Shows 'Play' button to start the match
     * Shows progress bar with the remaining time to the match end
     */
    if (tournamentData.isPlayerMatchReadyToPlay()) {
      console.debug('LiveBracketToastController: STATE ready-to-play - Player can start match');
      return 'ready-to-play';
    }

    /**
     * PRIORITY 3: If the player has finished the match and is waiting for the next round
     * Shows 'View Bracket' button to navigate to the bracket screen
     * Shows progress bar with the remaining time to the next round
     * This only applies AFTER the tournament has started
     */
    if (tournamentData.isPlayerWaitingForNextRound()) {
      console.debug(
        'LiveBracketToastController: STATE waiting-for-opponent - Waiting for next round'
      );
      return 'waiting-for-opponent';
    }

    /**
     * If none of the above conditions are met, we should hide the Toast
     */
    console.debug('LiveBracketToastController: STATE hidden - FALLBACK');
    return 'hidden';
  }

  /**
   * Helper function to asses if the user is ready to join the live bracket match
   * @param tournament tournament data from tournament-categories API
   * @returns boolean
   */
  static isPlayerReadyToPlay(tournament?: Tournament | null): boolean {
    return (
      LiveBracketToastController.getLiveBracketToastStateFromTournament(tournament) ===
      'ready-to-play'
    );
  }
}
